/**
 * frontend/src/pages/valuation/_EngagementTable.tsx
 *
 * VAL-HISTORY-2 — the engagement table, shared by Dashboard (in-progress
 * only) and History (everything this login can see). Moved out of
 * ValuationDashboard.tsx unchanged; showNextStep/showLastEdited toggle the
 * two screens' differing columns so neither has its own copy of the markup.
 *
 * VAL-HISTORY-3 — showVersions (History only) adds a per-row "Versions"
 * toggle on signed_off engagements; fetched only on first open via the
 * existing fetchSnapshotHistory, rendered through the existing
 * SnapshotHistory rather than a second copy of that table.
 *
 * VAL-HISTORY-ICONS-1 — the Next step text column and the Open link are gone;
 * one right-aligned Action column holds the shared ActionChip (_ActionChip).
 *
 * VAL-HISTORY-4a — the toggle passes showDocuments through to
 * SnapshotHistory so each version's issued documents render alongside it;
 * label switches "Versions ▼" / "Hide versions ▲" with aria-expanded.
 */
import { Fragment, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/contexts/AuthContext'
import { fetchSnapshotHistory } from '@/api/valuationB3'
import type { ValuationEngagement } from '@/types/valuation'
import type { SnapshotHistoryRow } from '@/types/valuationB3'
import { formatApiError } from '@/utils/httpErrors'
import { StatusBadge } from './_shared'
import { money } from '@/utils/format'
import { formatMediumDate } from '@/utils/date'
import { nextStep } from './_nextStep'
import { ActionChip } from './_ActionChip'
import { SnapshotHistory } from './_SnapshotHistory'

interface EngagementTableProps {
  engagements: ValuationEngagement[]
  showLastEdited?: boolean
  showVersions?: boolean
}

/**
 * One row plus its collapsed "Versions" expansion row. A component of its
 * own (not inline in the map) so the open/loading/rows state belongs to this
 * engagement alone, not the whole table.
 */
function EngagementRow({
  en,
  showLastEdited,
  showVersions,
  userRole,
  columnCount,
}: {
  en: ValuationEngagement
  showLastEdited: boolean
  showVersions: boolean
  userRole: string | undefined
  columnCount: number
}) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<SnapshotHistoryRow[] | null>(null)

  const low = en.value_kind === 'concluded' ? en.concluded_value_low : en.calculated_value_low
  const high = en.value_kind === 'concluded' ? en.concluded_value_high : en.calculated_value_high
  const canToggleVersions = showVersions && en.status === 'signed_off'
  const step = nextStep(en, userRole)

  function handleToggle() {
    if (open) {
      setOpen(false)
      return
    }
    setOpen(true)
    if (rows !== null || loading) return
    setLoading(true)
    setError(null)
    fetchSnapshotHistory(en.valuation_engagement_id)
      .then(res => setRows(res.snapshots))
      .catch((err: unknown) => setError(formatApiError(err)))
      .finally(() => setLoading(false))
  }

  return (
    <Fragment>
      <tr className="border-b last:border-0">
        <td className="py-2 pr-4 text-gray-900">{en.subject_company_name}</td>
        <td className="py-2 pr-4"><StatusBadge status={en.status} /></td>
        <td className="py-2 pr-4 whitespace-nowrap text-gray-700">
          {low === null || high === null ? '—' : `${money(low)} – ${money(high)}`}
        </td>
        <td className="py-2 pr-4 whitespace-nowrap text-gray-700">
          {en.value_kind === 'concluded' ? 'Conclusion' : 'Calculation'}
        </td>
        {showLastEdited && (
          <td className="py-2 pr-4 whitespace-nowrap text-xs text-gray-600">
            {formatMediumDate(en.updated_at)}
          </td>
        )}
        <td className="py-2 pr-4 whitespace-nowrap">
          {canToggleVersions && (
            <button
              type="button"
              onClick={handleToggle}
              aria-expanded={open}
              aria-controls={`version-history-${en.valuation_engagement_id}`}
              className="text-xs font-medium text-[#0d1f3c] underline"
            >
              {open ? 'Hide records ▲' : 'Records ▼'}
            </button>
          )}
        </td>
        <td className="py-2 text-right">
          <ActionChip label={step.label} route={step.route} kind={step.kind} raised={false} />
        </td>
      </tr>
      {canToggleVersions && open && (
        <tr className="border-b last:border-0" id={`version-history-${en.valuation_engagement_id}`}>
          <td colSpan={columnCount} className="bg-gray-50 p-3">
            {loading && <p className="text-xs text-gray-500">Loading…</p>}
            {!loading && error && <p className="text-xs text-red-700">{error}</p>}
            {!loading && !error && rows && (
              <SnapshotHistory rows={rows} engagementId={en.valuation_engagement_id} showDocuments />
            )}
          </td>
        </tr>
      )}
    </Fragment>
  )
}

export function _EngagementTable({
  engagements,
  showLastEdited = false,
  showVersions = false,
}: EngagementTableProps) {
  const { user } = useAuth()
  const columnCount = 4 + (showLastEdited ? 1 : 0) + 2
  return (
    <Card>
      <CardHeader>
        <CardTitle>Engagements</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-gray-500">
                <th className="py-2 pr-4 font-medium">Company</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Band</th>
                <th className="py-2 pr-4 font-medium">Deliverable</th>
                {showLastEdited && <th className="py-2 pr-4 font-medium">Last edited</th>}
                <th className="py-2 pr-4 text-right font-medium">{showVersions ? 'Records' : null}</th>
                <th className="py-2 text-right font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {engagements.map(en => (
                <EngagementRow
                  key={en.valuation_engagement_id}
                  en={en}
                  showLastEdited={showLastEdited}
                  showVersions={showVersions}
                  userRole={user?.role}
                  columnCount={columnCount}
                />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}
